import { cn } from '@/lib/utils'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import Link from 'next/link'
import type { TGender } from './constants'

/** Heading 5 (mobile) → Heading 2 (desktop) */
export const SECTION_TITLE_CLASS =
  'font-criteria text-[28px] font-semibold leading-[41px] tracking-[-0.02em] text-black lg:text-[55px] lg:font-medium lg:leading-[76px]'

/** Padding ngang chung của các section */
export const SECTION_X_CLASS = 'px-4 lg:px-15'

/** Lớp phủ gradient tối dần xuống đáy cho ảnh có chữ trắng */
export const IMAGE_GRADIENT_CLASS =
  'pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent from-55% to-black/55'

type GenderTabsProps = {
  value: TGender
  onChange: (gender: TGender) => void
  labels: Record<TGender, string>
  trackingPrefix: string
}

export function GenderTabs({
  value,
  onChange,
  labels,
  trackingPrefix,
}: GenderTabsProps) {
  return (
    <div role='tablist' className='flex gap-4'>
      {(Object.keys(labels) as TGender[]).map((gender) => {
        const active = gender === value
        return (
          <button
            key={gender}
            type='button'
            role='tab'
            aria-selected={active}
            onClick={() => onChange(gender)}
            ga-tracking-value={`${trackingPrefix}__tab__${gender.toLowerCase()}`}
            ga-tracking-label={labels[gender]}
            className={cn(
              'border-b-2 px-6 py-2 text-center font-sans text-sm font-medium leading-5 transition-colors',
              active
                ? 'border-neutral-800 text-black'
                : 'border-transparent text-neutral-500 hover:text-black'
            )}
          >
            {labels[gender]}
          </button>
        )
      })}
    </div>
  )
}

export function ViewAllLink({
  href,
  label,
  trackingValue,
}: {
  href: string
  label: string
  trackingValue: string
}) {
  return (
    <Link
      href={href}
      ga-tracking-value={trackingValue}
      ga-tracking-label={label}
      className='flex shrink-0 items-center gap-2.5 border-b border-neutral-800 px-4 py-2 font-sans text-sm font-medium leading-5 text-neutral-800 transition-opacity hover:opacity-70 lg:text-base lg:leading-6'
    >
      {label}
      <ArrowUpRight className='size-6' strokeWidth={1.5} />
    </Link>
  )
}

/** Link chữ trắng gạch chân, dùng trên ảnh (Stories, Chi tiết) */
export function ArrowTextLink({
  href,
  label,
  trackingValue,
  className,
}: {
  href: string
  label: string
  trackingValue: string
  className?: string
}) {
  return (
    <Link
      href={href}
      ga-tracking-value={trackingValue}
      ga-tracking-label={label}
      className={cn(
        'flex items-center justify-center gap-1 border-b border-neutral-50 py-1 font-criteria text-xs text-white transition-opacity hover:opacity-80',
        className
      )}
    >
      {label}
      <ArrowRight className='size-4' strokeWidth={1.5} />
    </Link>
  )
}

/** Nút trắng bo tròn (Hero, Banner) */
export function PillLink({
  href,
  label,
  trackingValue,
  className,
}: {
  href: string
  label: string
  trackingValue: string
  className?: string
}) {
  return (
    <Link
      href={href}
      ga-tracking-value={trackingValue}
      ga-tracking-label={label}
      className={cn(
        'flex items-center justify-center gap-2 rounded-full bg-white px-6 py-2 font-criteria text-sm uppercase text-black transition-opacity hover:opacity-90 lg:px-10 lg:py-[13px] lg:text-base',
        className
      )}
    >
      {label}
      <ArrowRight className='size-4 lg:size-6' strokeWidth={1.5} />
    </Link>
  )
}
